import { LogicalFilter } from "@refinedev/core";
import { useTable } from "@refinedev/react-table";
import { Row } from "@tanstack/react-table";
import dayjs from "dayjs";

import { usePlaceholderFilter } from "@/components/refine-ui/data-table/use-placeholder-filter";
import { useRowHovering } from "@/components/refine-ui/data-table/use-row-hovering";
import { useEventsColumns } from "@/pages/events/hooks/use-columns";
import { useEventRowActions } from "@/pages/events/hooks/use-row-actions";
import { IEventData } from "@/types/event";

export const useEventsTable = () => {
  const getRowActions = useEventRowActions();
  const hovering = useRowHovering<IEventData>({ getRowActions });
  const columns = useEventsColumns();
  const table = useTable<IEventData>({
    columns,
    columnResizeMode: "onChange",
    initialState: {
      columnPinning: {
        right: ["actions"],
      },
    },
    refineCoreProps: {
      resource: "events",
      pagination: {
        pageSize: 25,
      },
      sorters: {
        initial: [
          {
            field: "createdAt",
            order: "desc",
          },
        ],
      },
      filters: {
        initial: [
          {
            field: "startDate",
            operator: "gte",
            value: dayjs(new Date()).unix(),
          },
        ],
      },
      meta: {
        facetBy: ["geodata.name"],
        facetReturnParent: ["geodata.name"],
      },
    },
    meta: {
      querySuggestions: [
        {
          id: "venue.name",
          label: "venue",
        },
        {
          id: "lineup.name",
          label: "artist",
        },
        {
          id: "geodata.name",
          label: "city",
        },
        {
          id: "geodata.country",
          label: "country",
        },
        {
          id: "status",
          label: "status",
        },
        {
          id: "genres.name",
          label: "genres",
        },
        {
          id: "offers.type",
          label: "offers",
        },
        {
          id: "tags",
          label: "tags",
        },
      ],
      onDoubleClick: (_: React.MouseEvent, row: Row<IEventData>) => {
        window.open(`/events/edit/${row.original.document.id}`, "__blank");
      },
    },
  });

  const toggleTicketingFilter = (value: boolean) => {
    if (value) {
      table.refineCore.setFilters(
        [
          {
            field: "ticketing.available",
            operator: "eq",
            value: "true",
          },
        ],
        "merge",
      );
    } else {
      table.refineCore.setFilters((prev) =>
        prev.filter((f) => (f as any)?.field === "ticketing.available"),
      );
    }
  };

  const isTicketingFilterApplied = table.refineCore.filters.some(
    (f) => (f as LogicalFilter).field === "ticketing.available",
  );

  const placeholder = usePlaceholderFilter(table);

  return {
    table,
    hovering,
    globalFilters: {
      ticketing: {
        toggle: toggleTicketingFilter,
        isApplied: isTicketingFilterApplied,
      },
      placeholder: {
        toggle: placeholder.toggle,
        isApplied: placeholder.isApplied,
      },
    },
  };
};
